import {
  MANUSCRIPT_CONTENT_LIMITS,
  MANUSCRIPT_CONTENT_VERSION,
  MANUSCRIPT_FONT_FAMILIES,
  MANUSCRIPT_FONT_SIZE_LIMITS,
  MANUSCRIPT_LINE_SPACING_VALUES,
  manuscriptContentFromPlainText,
  normalizeManuscriptContent,
  plainTextFromManuscriptContent,
} from "./childrensBookManuscriptContent";

const INDENT_STEP = 720;

function safeObject(value) {
  return value &&
    typeof value === "object" &&
    !Array.isArray(value)
    ? value
    : {};
}

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(
    value,
    key
  );
}

const FONT_FAMILY_SET = new Set(MANUSCRIPT_FONT_FAMILIES);

function normalizedFontFamily(value) {
  const family = typeof value === "string"
    ? value.trim()
    : "";

  return FONT_FAMILY_SET.has(family)
    ? family
    : "";
}

function normalizedFontSize(value) {
  const size = Number(value);

  return Number.isInteger(size) &&
    size >= MANUSCRIPT_FONT_SIZE_LIMITS.minimum &&
    size <= MANUSCRIPT_FONT_SIZE_LIMITS.maximum
      ? size
      : 0;
}

function sameMarks(left, right) {
  return Boolean(left?.bold) === Boolean(right?.bold) &&
    Boolean(left?.italic) === Boolean(right?.italic) &&
    Boolean(left?.underline) === Boolean(right?.underline) &&
    Boolean(left?.highlight) === Boolean(right?.highlight) &&
    normalizedFontFamily(left?.fontFamily) ===
      normalizedFontFamily(right?.fontFamily) &&
    normalizedFontSize(left?.fontSize) ===
      normalizedFontSize(right?.fontSize);
}

function withOptionalTypography(run, source = {}) {
  const fontFamily = normalizedFontFamily(source.fontFamily);
  const fontSize = normalizedFontSize(source.fontSize);

  if (fontFamily) {
    run.fontFamily = fontFamily;
  }

  if (fontSize) {
    run.fontSize = fontSize;
  }

  return run;
}

function emptyRun(marks = {}) {
  return withOptionalTypography({
    text: "",
    bold: Boolean(marks.bold),
    italic: Boolean(marks.italic),
    underline: Boolean(marks.underline),
    highlight: Boolean(marks.highlight),
  }, marks);
}

export function normalizeManuscriptEditorRuns(value) {
  const source = Array.isArray(value)
    ? value
    : [];

  const runs = [];

  source.forEach((item) => {
    const run = safeObject(item);
    const next = withOptionalTypography({
      text:
        typeof run.text === "string"
          ? run.text.replace(/\f/g, "")
          : "",
      bold: Boolean(run.bold),
      italic: Boolean(run.italic),
      underline: Boolean(run.underline),
      highlight: Boolean(run.highlight),
    }, run);

    const previous = runs[runs.length - 1];

    if (previous && sameMarks(previous, next)) {
      previous.text += next.text;
    } else {
      runs.push(next);
    }
  });

  return runs.length
    ? runs
    : [emptyRun()];
}


function blockTextFromRuns(runs) {
  return normalizeManuscriptEditorRuns(runs)
    .map((run) => run.text)
    .join("");
}

function sliceRunsBetweenOffsets(
  runs,
  requestedStart,
  requestedEnd
) {
  const source = normalizeManuscriptEditorRuns(runs);
  const total = source.reduce(
    (sum, run) => sum + run.text.length,
    0
  );
  const start = Math.max(
    0,
    Math.min(Number(requestedStart) || 0, total)
  );
  const end = Math.max(
    start,
    Math.min(Number(requestedEnd) || start, total)
  );
  const selected = [];
  let cursor = 0;
  let marks = source[0] || emptyRun();

  source.forEach((run) => {
    const runStart = cursor;
    const runEnd = runStart + run.text.length;
    cursor = runEnd;

    if (start >= runStart && start <= runEnd) {
      marks = run;
    }

    if (runEnd <= start || runStart >= end) {
      return;
    }

    selected.push({
      ...run,
      text: run.text.slice(
        Math.max(0, start - runStart),
        Math.min(run.text.length, end - runStart)
      ),
    });
  });

  return normalizeManuscriptEditorRuns(
    selected.length
      ? selected
      : [emptyRun(marks)]
  );
}

function paragraphRangesForBlock(block) {
  const text = blockTextFromRuns(block?.runs);
  const ranges = [];
  const separator = /\n{2,}/g;
  let start = 0;
  let match = separator.exec(text);

  while (match) {
    ranges.push({
      start,
      end: match.index,
    });

    // Two newlines become the deterministic separator between blocks.
    // Any additional newlines remain attached to the following paragraph.
    start = match.index + 2;
    match = separator.exec(text);
  }

  ranges.push({
    start,
    end: text.length,
  });

  return ranges;
}

function paragraphIndexesForSelection(ranges, start, end) {
  if (!ranges.length) {
    return [];
  }

  if (start === end) {
    const index = ranges.findIndex(
      (range, rangeIndex) =>
        start >= range.start &&
        (
          start <= range.end ||
          rangeIndex === ranges.length - 1
        )
    );

    return [index >= 0 ? index : ranges.length - 1];
  }

  const indexes = ranges
    .map((range, index) => ({ range, index }))
    .filter(({ range }) =>
      range.end > start && range.start < end
    )
    .map(({ index }) => index);

  if (indexes.length) {
    return indexes;
  }

  const fallback = ranges.findIndex(
    (range) => start >= range.start && start <= range.end
  );

  return [fallback >= 0 ? fallback : 0];
}

function nextBlockIdFromUsed(used) {
  let index = 1;
  let candidate = `editor-block-${index}`;

  while (used.has(candidate)) {
    index += 1;
    candidate = `editor-block-${index}`;
  }

  used.add(candidate);
  return candidate;
}

function updateSelectedParagraphs(
  value,
  blockId,
  requestedStart,
  requestedEnd,
  updater
) {
  const content = normalizedContentOrNull(value);

  if (!content) {
    return null;
  }

  const blockIndex = content.blocks.findIndex(
    (block) => block.id === blockId
  );

  if (blockIndex < 0) {
    return {
      content,
      selection: {
        blockId,
        start: 0,
        end: 0,
      },
    };
  }

  const block = content.blocks[blockIndex];

  if (block.type === "page_break") {
    return {
      content,
      selection: {
        blockId,
        start: 0,
        end: 0,
      },
    };
  }

  const text = blockTextFromRuns(block.runs);
  const total = text.length;
  const start = Math.max(
    0,
    Math.min(Number(requestedStart) || 0, total)
  );
  const end = Math.max(
    start,
    Math.min(Number(requestedEnd) || start, total)
  );
  const ranges = paragraphRangesForBlock(block);
  const selectedIndexes = paragraphIndexesForSelection(
    ranges,
    start,
    end
  );
  const selected = new Set(selectedIndexes);
  const anchorIndex = selectedIndexes[0] || 0;

  if (ranges.length === 1) {
    const nextContent = withUpdatedBlock(
      content,
      blockId,
      (current) => updater(current)
    );

    return {
      content: nextContent,
      selection: {
        blockId,
        start,
        end,
      },
    };
  }

  const used = new Set(
    content.blocks.map((item) => String(item?.id || ""))
  );
  const paragraphBlocks = ranges.map((range, rangeIndex) => {
    const id = rangeIndex === anchorIndex
      ? blockId
      : nextBlockIdFromUsed(used);
    const runs = sliceRunsBetweenOffsets(
      block.runs,
      range.start,
      range.end
    );
    const base = rangeIndex === 0
      ? {
          ...block,
          id,
          runs,
        }
      : continuationBlock(block, id, runs);

    return selected.has(rangeIndex)
      ? updater(base)
      : base;
  });
  const blocks = [...content.blocks];

  blocks.splice(blockIndex, 1, ...paragraphBlocks);

  const nextContent = normalizedContentOrNull({
    version: MANUSCRIPT_CONTENT_VERSION,
    blocks,
  });
  const anchorRange = ranges[anchorIndex];

  return nextContent
    ? {
        content: nextContent,
        selection: {
          blockId,
          start: Math.max(0, start - anchorRange.start),
          end: Math.max(
            0,
            Math.min(end, anchorRange.end) - anchorRange.start
          ),
        },
      }
    : null;
}

function normalizedContentOrNull(value) {
  const result = normalizeManuscriptContent(value);

  return result.ok && result.content
    ? result.content
    : null;
}

export function manuscriptEditorStateFromEntry(value) {
  const entry = safeObject(value);

  if (hasOwn(entry, "manuscriptContent")) {
    const result = normalizeManuscriptContent(
      entry.manuscriptContent
    );

    if (!result.ok || !result.content) {
      return {
        ok: false,
        content: null,
        errors: result.errors,
      };
    }

    return {
      ok: true,
      content: result.content,
      errors: [],
    };
  }

  return {
    ok: true,
    content: manuscriptContentFromPlainText(
      typeof entry.manuscriptText === "string"
        ? entry.manuscriptText
        : ""
    ),
    errors: [],
  };
}

function nextEditorBlockId(content) {
  const used = new Set(
    (Array.isArray(content?.blocks)
      ? content.blocks
      : []
    ).map((block) => String(block?.id || ""))
  );

  let index = 1;
  let candidate = `editor-block-${index}`;

  while (used.has(candidate)) {
    index += 1;
    candidate = `editor-block-${index}`;
  }

  return candidate;
}

function defaultParagraph(id) {
  return {
    id,
    type: "paragraph",
    alignment: "left",
    indentation: {
      left: 0,
      right: 0,
      firstLine: 0,
      hanging: 0,
    },
    runs: [emptyRun()],
  };
}

export function expandManuscriptEditorParagraphBlocks(value) {
  const content = normalizedContentOrNull(value);

  if (!content) {
    return null;
  }

  const used = new Set(
    content.blocks.map((block) => String(block?.id || ""))
  );
  const blocks = [];

  content.blocks.forEach((block) => {
    if (block.type === "page_break") {
      blocks.push(block);
      return;
    }

    const ranges = paragraphRangesForBlock(block);

    if (ranges.length <= 1) {
      blocks.push(block);
      return;
    }

    ranges.forEach((range, index) => {
      const id = index === 0
        ? block.id
        : nextBlockIdFromUsed(used);
      const runs = sliceRunsBetweenOffsets(
        block.runs,
        range.start,
        range.end
      );

      blocks.push(
        index === 0
          ? {
              ...block,
              id,
              runs,
            }
          : continuationBlock(block, id, runs)
      );
    });
  });

  return normalizedContentOrNull({
    version: MANUSCRIPT_CONTENT_VERSION,
    blocks,
  });
}

export function editableManuscriptContent(value) {
  const content = expandManuscriptEditorParagraphBlocks(value);

  if (!content) {
    return null;
  }

  if (content.blocks.length) {
    return content;
  }

  return {
    version: MANUSCRIPT_CONTENT_VERSION,
    blocks: [
      defaultParagraph(
        nextEditorBlockId(content)
      ),
    ],
  };
}

export function manuscriptEntryFieldsFromEditorContent(value) {
  const result = normalizeManuscriptContent(value);

  if (!result.ok || !result.content) {
    return {
      ok: false,
      errors: result.errors,
    };
  }

  return {
    ok: true,
    errors: [],
    manuscriptContent: result.content,
    manuscriptText: plainTextFromManuscriptContent(
      result.content
    ),
  };
}

function withUpdatedBlock(value, blockId, updater) {
  const content = normalizedContentOrNull(value);

  if (!content) {
    return null;
  }

  let changed = false;

  const blocks = content.blocks.map((block) => {
    if (block.id !== blockId) {
      return block;
    }

    changed = true;
    return updater(block);
  });

  if (!changed) {
    return content;
  }

  return normalizedContentOrNull({
    version: MANUSCRIPT_CONTENT_VERSION,
    blocks,
  });
}

export function replaceManuscriptEditorBlockRuns(
  value,
  blockId,
  runs
) {
  return withUpdatedBlock(
    value,
    blockId,
    (block) => ({
      ...block,
      runs: normalizeManuscriptEditorRuns(runs),
    })
  );
}

function splitRunsAtOffset(runs, requestedOffset) {
  const source = normalizeManuscriptEditorRuns(runs);
  const total = source.reduce(
    (sum, run) => sum + run.text.length,
    0
  );
  const offset = Math.max(
    0,
    Math.min(Number(requestedOffset) || 0, total)
  );

  const before = [];
  const after = [];
  let cursor = 0;
  let splitMarks = source[0] || emptyRun();

  source.forEach((run) => {
    const start = cursor;
    const end = start + run.text.length;
    cursor = end;

    if (offset >= start && offset <= end) {
      splitMarks = run;
    }

    if (end <= offset) {
      before.push({ ...run });
      return;
    }

    if (start >= offset) {
      after.push({ ...run });
      return;
    }

    const local = offset - start;

    before.push({
      ...run,
      text: run.text.slice(0, local),
    });

    after.push({
      ...run,
      text: run.text.slice(local),
    });
  });

  return {
    before: normalizeManuscriptEditorRuns(
      before.length
        ? before
        : [emptyRun(splitMarks)]
    ),
    after: normalizeManuscriptEditorRuns(
      after.length
        ? after
        : [emptyRun(splitMarks)]
    ),
  };
}

function plainTextRun(text, marks) {
  return withOptionalTypography({
    text,
    bold: Boolean(marks?.bold),
    italic: Boolean(marks?.italic),
    underline: Boolean(marks?.underline),
    highlight: Boolean(marks?.highlight),
  }, marks);
}

export function insertManuscriptEditorPlainTextAtSelection(
  value,
  blockId,
  requestedStart,
  requestedEnd,
  requestedText
) {
  const content = normalizedContentOrNull(value);

  if (!content) {
    return null;
  }

  const blockIndex = content.blocks.findIndex(
    (block) => block.id === blockId
  );

  if (blockIndex < 0) {
    return null;
  }

  const block = content.blocks[blockIndex];

  if (block.type === "page_break") {
    return null;
  }

  const runs = normalizeManuscriptEditorRuns(block.runs);
  const total = runs.reduce(
    (sum, run) => sum + run.text.length,
    0
  );
  const rawStart = Number(requestedStart);
  const rawEnd = Number(requestedEnd);
  const first = Number.isFinite(rawStart)
    ? Math.trunc(rawStart)
    : 0;
  const second = Number.isFinite(rawEnd)
    ? Math.trunc(rawEnd)
    : first;
  const start = Math.max(
    0,
    Math.min(Math.min(first, second), total)
  );
  const end = Math.max(
    0,
    Math.min(Math.max(first, second), total)
  );
  const text = String(requestedText || "")
    .replace(/\u200b/g, "")
    .replace(/\r\n?/g, "\n")
    .replace(/\f/g, "");
  const paragraphs = text.split("\n\n");
  const splitAtStart = splitRunsAtOffset(runs, start);
  const insertionMarks =
    splitAtStart.before[splitAtStart.before.length - 1] ||
    splitAtStart.after[0] ||
    emptyRun();
  const before = sliceRunsBetweenOffsets(
    runs,
    0,
    start
  );
  const after = sliceRunsBetweenOffsets(
    runs,
    end,
    total
  );

  function pastedRuns(paragraphText) {
    return normalizeManuscriptEditorRuns([
      plainTextRun(paragraphText, insertionMarks),
    ]);
  }

  if (paragraphs.length === 1) {
    const blocks = [...content.blocks];
    const targetOffset = start + paragraphs[0].length;

    blocks[blockIndex] = {
      ...block,
      runs: normalizeManuscriptEditorRuns([
        ...before,
        ...pastedRuns(paragraphs[0]),
        ...after,
      ]),
    };

    const nextContent = normalizedContentOrNull({
      version: MANUSCRIPT_CONTENT_VERSION,
      blocks,
    });

    return nextContent
      ? {
          content: nextContent,
          selection: {
            blockId,
            start: targetOffset,
            end: targetOffset,
          },
        }
      : null;
  }

  const used = new Set(
    content.blocks.map((item) => String(item?.id || ""))
  );
  const replacementBlocks = [
    {
      ...block,
      runs: normalizeManuscriptEditorRuns([
        ...before,
        ...pastedRuns(paragraphs[0]),
      ]),
    },
  ];
  let targetBlockId = blockId;

  paragraphs.slice(1).forEach((paragraphText, index) => {
    const id = nextBlockIdFromUsed(used);
    const isLast = index === paragraphs.length - 2;
    const runsForBlock = isLast
      ? normalizeManuscriptEditorRuns([
          ...pastedRuns(paragraphText),
          ...after,
        ])
      : pastedRuns(paragraphText);

    replacementBlocks.push(
      continuationBlock(
        block,
        id,
        runsForBlock
      )
    );

    if (isLast) {
      targetBlockId = id;
    }
  });

  const blocks = [...content.blocks];

  blocks.splice(
    blockIndex,
    1,
    ...replacementBlocks
  );

  const nextContent = normalizedContentOrNull({
    version: MANUSCRIPT_CONTENT_VERSION,
    blocks,
  });
  const targetOffset =
    paragraphs[paragraphs.length - 1].length;

  return nextContent
    ? {
        content: nextContent,
        selection: {
          blockId: targetBlockId,
          start: targetOffset,
          end: targetOffset,
        },
      }
    : null;
}

function continuationBlock(source, id, runs) {
  if (
    source.type === "paragraph" ||
    source.type === "list_item" ||
    source.type === "blockquote"
  ) {
    return {
      ...source,
      id,
      runs,
    };
  }

  return {
    ...defaultParagraph(id),
    alignment: source.alignment || "left",
    indentation: {
      left: Number(source.indentation?.left) || 0,
      right: Number(source.indentation?.right) || 0,
      firstLine: Number(source.indentation?.firstLine) || 0,
      hanging: Number(source.indentation?.hanging) || 0,
    },
    runs,
  };
}

export function splitManuscriptEditorBlock(
  value,
  blockId,
  offset
) {
  const content = normalizedContentOrNull(value);

  if (!content) {
    return null;
  }

  const index = content.blocks.findIndex(
    (block) => block.id === blockId
  );

  if (index < 0) {
    return content;
  }

  const block = content.blocks[index];

  if (block.type === "page_break") {
    return content;
  }

  const split = splitRunsAtOffset(
    block.runs,
    offset
  );
  const nextId = nextEditorBlockId(content);
  const blocks = [...content.blocks];

  blocks.splice(
    index,
    1,
    {
      ...block,
      runs: split.before,
    },
    continuationBlock(
      block,
      nextId,
      split.after
    )
  );

  const nextContent = normalizedContentOrNull({
    version: MANUSCRIPT_CONTENT_VERSION,
    blocks,
  });

  return nextContent
    ? {
        content: nextContent,
        nextBlockId: nextId,
      }
    : null;
}

export function mergeManuscriptEditorBlockWithPrevious(
  value,
  blockId
) {
  const content = normalizedContentOrNull(value);

  if (!content) {
    return null;
  }

  const index = content.blocks.findIndex(
    (block) => block.id === blockId
  );

  if (index <= 0) {
    return {
      content,
      targetBlockId: blockId,
      targetOffset: 0,
    };
  }

  const current = content.blocks[index];
  const previous = content.blocks[index - 1];

  if (
    current.type === "page_break" ||
    previous.type === "page_break"
  ) {
    return {
      content,
      targetBlockId: blockId,
      targetOffset: 0,
    };
  }

  const targetOffset = previous.runs.reduce(
    (sum, run) => sum + run.text.length,
    0
  );
  const blocks = [...content.blocks];

  blocks.splice(
    index - 1,
    2,
    {
      ...previous,
      runs: normalizeManuscriptEditorRuns([
        ...previous.runs,
        ...current.runs,
      ]),
    }
  );

  const nextContent = normalizedContentOrNull({
    version: MANUSCRIPT_CONTENT_VERSION,
    blocks,
  });

  return nextContent
    ? {
        content: nextContent,
        targetBlockId: previous.id,
        targetOffset,
      }
    : null;
}

function applyMarkToRuns(runs, start, end, mark, value) {
  const next = [];
  let cursor = 0;

  runs.forEach((run) => {
    const runStart = cursor;
    const runEnd = runStart + run.text.length;
    cursor = runEnd;

    if (runEnd <= start || runStart >= end) {
      next.push({ ...run });
      return;
    }

    const localStart = Math.max(0, start - runStart);
    const localEnd = Math.min(
      run.text.length,
      end - runStart
    );

    if (localStart > 0) {
      next.push({
        ...run,
        text: run.text.slice(0, localStart),
      });
    }

    next.push({
      ...run,
      text: run.text.slice(localStart, localEnd),
      [mark]: value,
    });

    if (localEnd < run.text.length) {
      next.push({
        ...run,
        text: run.text.slice(localEnd),
      });
    }
  });

  return normalizeManuscriptEditorRuns(next);
}

export function toggleManuscriptEditorInlineMark(
  value,
  blockId,
  startOffset,
  endOffset,
  mark
) {
  if (!["bold", "italic", "underline", "highlight"].includes(mark)) {
    return normalizedContentOrNull(value);
  }

  return withUpdatedBlock(
    value,
    blockId,
    (block) => {
      if (block.type === "page_break") {
        return block;
      }

      const runs = normalizeManuscriptEditorRuns(
        block.runs
      );
      const total = runs.reduce(
        (sum, run) => sum + run.text.length,
        0
      );
      const start = Math.max(
        0,
        Math.min(Number(startOffset) || 0, total)
      );
      const end = Math.max(
        start,
        Math.min(Number(endOffset) || start, total)
      );

      if (start === end) {
        let cursor = 0;
        let targetStart = 0;
        let targetEnd = 0;
        let targetValue = false;

        for (const run of runs) {
          const runStart = cursor;
          const runEnd = runStart + run.text.length;
          cursor = runEnd;

          if (start <= runEnd) {
            targetStart = runStart;
            targetEnd = runEnd;
            targetValue = Boolean(run[mark]);
            break;
          }
        }

        if (targetStart === targetEnd) {
          return {
            ...block,
            runs: runs.map((run, index) =>
              index === 0
                ? {
                    ...run,
                    [mark]: !targetValue,
                  }
                : run
            ),
          };
        }

        return {
          ...block,
          runs: applyMarkToRuns(
            runs,
            targetStart,
            targetEnd,
            mark,
            !targetValue
          ),
        };
      }

      const selectedRuns = applyMarkToRuns(
        runs,
        start,
        end,
        mark,
        true
      );
      let cursor = 0;
      let allMarked = true;

      runs.forEach((run) => {
        const runStart = cursor;
        const runEnd = runStart + run.text.length;
        cursor = runEnd;

        if (runEnd > start && runStart < end && !run[mark]) {
          allMarked = false;
        }
      });

      return {
        ...block,
        runs: allMarked
          ? applyMarkToRuns(
              runs,
              start,
              end,
              mark,
              false
            )
          : selectedRuns,
      };
    }
  );
}

function applyTypographyToRuns(
  runs,
  start,
  end,
  property,
  requestedValue
) {
  const next = [];
  let cursor = 0;

  runs.forEach((run) => {
    const runStart = cursor;
    const runEnd = runStart + run.text.length;
    cursor = runEnd;

    if (runEnd <= start || runStart >= end) {
      next.push({ ...run });
      return;
    }

    const localStart = Math.max(0, start - runStart);
    const localEnd = Math.min(run.text.length, end - runStart);

    if (localStart > 0) {
      next.push({
        ...run,
        text: run.text.slice(0, localStart),
      });
    }

    const styled = {
      ...run,
      text: run.text.slice(localStart, localEnd),
    };

    if (requestedValue === undefined) {
      delete styled[property];
    } else {
      styled[property] = requestedValue;
    }

    next.push(styled);

    if (localEnd < run.text.length) {
      next.push({
        ...run,
        text: run.text.slice(localEnd),
      });
    }
  });

  return normalizeManuscriptEditorRuns(next);
}

export function setManuscriptEditorInlineTypography(
  value,
  blockId,
  startOffset,
  endOffset,
  property,
  requestedValue
) {
  if (!["fontFamily", "fontSize"].includes(property)) {
    return normalizedContentOrNull(value);
  }

  const normalizedValue = property === "fontFamily"
    ? (normalizedFontFamily(requestedValue) || undefined)
    : (normalizedFontSize(requestedValue) || undefined);

  return withUpdatedBlock(
    value,
    blockId,
    (block) => {
      if (block.type === "page_break") {
        return block;
      }

      const runs = normalizeManuscriptEditorRuns(block.runs);
      const total = runs.reduce(
        (sum, run) => sum + run.text.length,
        0
      );
      const start = Math.max(
        0,
        Math.min(Number(startOffset) || 0, total)
      );
      const end = Math.max(
        start,
        Math.min(Number(endOffset) || start, total)
      );

      if (start === end) {
        let cursor = 0;
        let targetIndex = runs.length - 1;

        runs.some((run, index) => {
          const runEnd = cursor + run.text.length;
          const contains = start <= runEnd;
          cursor = runEnd;

          if (contains) {
            targetIndex = index;
          }

          return contains;
        });

        return {
          ...block,
          runs: normalizeManuscriptEditorRuns(
            runs.map((run, index) => {
              if (index !== targetIndex) {
                return run;
              }

              const styled = { ...run };

              if (normalizedValue === undefined) {
                delete styled[property];
              } else {
                styled[property] = normalizedValue;
              }

              return styled;
            })
          ),
        };
      }

      return {
        ...block,
        runs: applyTypographyToRuns(
          runs,
          start,
          end,
          property,
          normalizedValue
        ),
      };
    }
  );
}

export function manuscriptEditorTypographyAtOffset(
  value,
  blockId,
  requestedOffset
) {
  const content = normalizedContentOrNull(value);
  const block = content?.blocks.find(
    (item) => item.id === blockId
  );

  if (!block || block.type === "page_break") {
    return {
      fontFamily: "",
      fontSize: 0,
    };
  }

  const runs = normalizeManuscriptEditorRuns(block.runs);
  const total = runs.reduce(
    (sum, run) => sum + run.text.length,
    0
  );
  const offset = Math.max(
    0,
    Math.min(Number(requestedOffset) || 0, total)
  );
  let cursor = 0;
  let selected = runs[runs.length - 1] || emptyRun();

  runs.some((run) => {
    const runEnd = cursor + run.text.length;
    const contains = offset <= runEnd;
    cursor = runEnd;

    if (contains) {
      selected = run;
    }

    return contains;
  });

  return {
    fontFamily: normalizedFontFamily(selected.fontFamily),
    fontSize: normalizedFontSize(selected.fontSize),
  };
}

export function setManuscriptEditorSelectedParagraphPresentation(
  value,
  blockId,
  startOffset,
  endOffset,
  presentation
) {
  return updateSelectedParagraphs(
    value,
    blockId,
    startOffset,
    endOffset,
    (block) => blockPresentation(block, presentation)
  );
}

export function setManuscriptEditorSelectedParagraphAlignment(
  value,
  blockId,
  startOffset,
  endOffset,
  alignment
) {
  if (!["left", "center", "right", "justify"].includes(alignment)) {
    return null;
  }

  return updateSelectedParagraphs(
    value,
    blockId,
    startOffset,
    endOffset,
    (block) => ({
      ...block,
      alignment,
    })
  );
}

export function setManuscriptEditorSelectedParagraphLineSpacing(
  value,
  blockId,
  startOffset,
  endOffset,
  requestedLineSpacing
) {
  const clear =
    requestedLineSpacing ===
      undefined ||
    requestedLineSpacing ===
      null ||
    requestedLineSpacing ===
      "";

  const lineSpacing =
    clear
      ? null
      : Number(
          requestedLineSpacing
        );

  if (
    !clear &&
    !MANUSCRIPT_LINE_SPACING_VALUES.includes(
      lineSpacing
    )
  ) {
    return null;
  }

  return updateSelectedParagraphs(
    value,
    blockId,
    startOffset,
    endOffset,
    (block) => {
      const next = {
        ...block,
      };

      if (clear) {
        delete next.lineSpacing;
      } else {
        next.lineSpacing =
          lineSpacing;
      }

      return next;
    }
  );
}


export function changeManuscriptEditorSelectedParagraphIndent(
  value,
  blockId,
  startOffset,
  endOffset,
  direction
) {
  const delta = direction === "outdent"
    ? -INDENT_STEP
    : INDENT_STEP;

  return updateSelectedParagraphs(
    value,
    blockId,
    startOffset,
    endOffset,
    (block) => {
      const indentation = {
        left: Number(block.indentation?.left) || 0,
        right: Number(block.indentation?.right) || 0,
        firstLine: Number(block.indentation?.firstLine) || 0,
        hanging: Number(block.indentation?.hanging) || 0,
      };

      indentation.left = Math.max(
        0,
        Math.min(
          MANUSCRIPT_CONTENT_LIMITS.maximumIndentation,
          indentation.left + delta
        )
      );

      return {
        ...block,
        indentation,
        ...(block.type === "list_item"
          ? {
              list: {
                ...block.list,
                level: Math.max(
                  0,
                  Math.min(
                    MANUSCRIPT_CONTENT_LIMITS.maximumListLevel,
                    (Number(block.list?.level) || 0) +
                      (direction === "outdent" ? -1 : 1)
                  )
                ),
              },
            }
          : {}),
      };
    }
  );
}

function blockPresentation(block, presentation) {
  const base = {
    ...block,
    alignment: block.alignment || "left",
    indentation: {
      left: Number(block.indentation?.left) || 0,
      right: Number(block.indentation?.right) || 0,
      firstLine: Number(block.indentation?.firstLine) || 0,
      hanging: Number(block.indentation?.hanging) || 0,
    },
  };

  function withoutMetadata(...keys) {
    const next = { ...base };

    keys.forEach((key) => {
      delete next[key];
    });

    return next;
  }

  if (presentation === "paragraph") {
    return {
      ...withoutMetadata("level", "list"),
      type: "paragraph",
    };
  }

  if (/^heading-[1-3]$/.test(presentation)) {
    return {
      ...withoutMetadata("list"),
      type: "heading",
      level: Number(presentation.slice(-1)),
    };
  }

  if (presentation === "blockquote") {
    return {
      ...withoutMetadata("level", "list"),
      type: "blockquote",
    };
  }

  if (presentation === "bullet" || presentation === "ordered") {
    return {
      ...withoutMetadata("level"),
      type: "list_item",
      list: {
        type: presentation,
        level: Number(block.list?.level) || 0,
        numberingId:
          presentation === "ordered"
            ? String(block.list?.numberingId || "editor-list")
            : "",
      },
    };
  }

  return base;
}

export function setManuscriptEditorBlockPresentation(
  value,
  blockId,
  presentation
) {
  return withUpdatedBlock(
    value,
    blockId,
    (block) =>
      block.type === "page_break"
        ? block
        : blockPresentation(block, presentation)
  );
}

export function setManuscriptEditorBlockAlignment(
  value,
  blockId,
  alignment
) {
  if (!["left", "center", "right", "justify"].includes(alignment)) {
    return normalizedContentOrNull(value);
  }

  return withUpdatedBlock(
    value,
    blockId,
    (block) =>
      block.type === "page_break"
        ? block
        : {
            ...block,
            alignment,
          }
  );
}


export function setManuscriptEditorWholeDocumentAlignment(
  value,
  alignment
) {
  if (
    ![
      "left",
      "center",
      "right",
      "justify",
    ].includes(alignment)
  ) {
    return editableManuscriptContent(
      value
    );
  }

  const content =
    editableManuscriptContent(
      value
    );

  if (!content) {
    return null;
  }

  const bodyTypes =
    new Set([
      "paragraph",
      "blockquote",
      "list_item",
    ]);

  return normalizedContentOrNull({
    version:
      MANUSCRIPT_CONTENT_VERSION,
    blocks:
      content.blocks.map(
        (block) =>
          bodyTypes.has(
            block?.type
          )
            ? {
                ...block,
                alignment,
              }
            : block
      ),
  });
}

export function changeManuscriptEditorBlockIndent(
  value,
  blockId,
  direction
) {
  const delta = direction === "outdent"
    ? -INDENT_STEP
    : INDENT_STEP;

  return withUpdatedBlock(
    value,
    blockId,
    (block) => {
      if (block.type === "page_break") {
        return block;
      }

      const indentation = {
        left: Number(block.indentation?.left) || 0,
        right: Number(block.indentation?.right) || 0,
        firstLine: Number(block.indentation?.firstLine) || 0,
        hanging: Number(block.indentation?.hanging) || 0,
      };

      indentation.left = Math.max(
        0,
        Math.min(
          MANUSCRIPT_CONTENT_LIMITS.maximumIndentation,
          indentation.left + delta
        )
      );

      return {
        ...block,
        indentation,
        ...(block.type === "list_item"
          ? {
              list: {
                ...block.list,
                level: Math.max(
                  0,
                  Math.min(
                    MANUSCRIPT_CONTENT_LIMITS.maximumListLevel,
                    (Number(block.list?.level) || 0) +
                      (direction === "outdent" ? -1 : 1)
                  )
                ),
              },
            }
          : {}),
      };
    }
  );
}

export function manuscriptEditorBlockPresentation(block) {
  if (block?.type === "heading") {
    return `heading-${Math.max(1, Math.min(3, Number(block.level) || 1))}`;
  }

  if (block?.type === "list_item") {
    return block.list?.type === "ordered"
      ? "ordered"
      : "bullet";
  }

  if (block?.type === "blockquote") {
    return "blockquote";
  }

  return "paragraph";
}

/*
 * Normalize one browser-style manuscript selection across structured
 * manuscript blocks without mutating manuscript authority.
 *
 * This is deliberately a model-only boundary. The current editor DOM
 * will adopt it separately once the cross-block contract is proven.
 */
export function normalizeManuscriptEditorSurfaceSelection(
  value,
  selection = {}
) {
  const content = normalizedContentOrNull(value);

  if (!content) {
    return null;
  }

  const anchorBlockId =
    String(
      selection?.anchorBlockId || ""
    ).trim();

  const focusBlockId =
    String(
      selection?.focusBlockId || ""
    ).trim();

  if (
    !anchorBlockId ||
    !focusBlockId
  ) {
    return null;
  }

  const anchorIndex =
    content.blocks.findIndex(
      (block) =>
        String(block?.id || "") ===
        anchorBlockId
    );

  const focusIndex =
    content.blocks.findIndex(
      (block) =>
        String(block?.id || "") ===
        focusBlockId
    );

  if (
    anchorIndex < 0 ||
    focusIndex < 0
  ) {
    return null;
  }

  function blockLength(index) {
    const block =
      content.blocks[index];

    if (
      !block ||
      block.type === "page_break"
    ) {
      return 0;
    }

    return blockTextFromRuns(
      block.runs
    ).length;
  }

  function boundedOffset(
    index,
    requested
  ) {
    const raw =
      Number(requested);

    const offset =
      Number.isFinite(raw)
        ? Math.trunc(raw)
        : 0;

    return Math.max(
      0,
      Math.min(
        offset,
        blockLength(index)
      )
    );
  }

  const anchorOffset =
    boundedOffset(
      anchorIndex,
      selection?.anchorOffset
    );

  const focusOffset =
    boundedOffset(
      focusIndex,
      selection?.focusOffset
    );

  const anchorFirst =
    anchorIndex < focusIndex ||
    (
      anchorIndex === focusIndex &&
      anchorOffset <= focusOffset
    );

  const startBlockIndex =
    anchorFirst
      ? anchorIndex
      : focusIndex;

  const endBlockIndex =
    anchorFirst
      ? focusIndex
      : anchorIndex;

  const startBlockId =
    anchorFirst
      ? anchorBlockId
      : focusBlockId;

  const endBlockId =
    anchorFirst
      ? focusBlockId
      : anchorBlockId;

  const startOffset =
    anchorFirst
      ? anchorOffset
      : focusOffset;

  const endOffset =
    anchorFirst
      ? focusOffset
      : anchorOffset;

  const selectedBlocks =
    content.blocks.slice(
      startBlockIndex,
      endBlockIndex + 1
    );

  return {
    startBlockId,
    startBlockIndex,
    startOffset,

    endBlockId,
    endBlockIndex,
    endOffset,

    selectedBlockIds:
      selectedBlocks.map(
        (block) =>
          String(block?.id || "")
      ),

    crossesPageBreak:
      selectedBlocks.some(
        (block) =>
          block?.type ===
          "page_break"
      ),

    collapsed:
      startBlockIndex ===
        endBlockIndex &&
      startOffset ===
        endOffset,
  };
}


/*
 * Return the plain manuscript text represented by a normalized
 * cross-block selection.
 *
 * Explicit page-break selections fail closed for now because deleting
 * or replacing structural page breaks requires its own author-facing
 * decision and must not be inferred by the editor.
 */
export function manuscriptEditorSurfaceSelectionText(
  value,
  selection = {}
) {
  const content =
    normalizedContentOrNull(value);

  const range =
    normalizeManuscriptEditorSurfaceSelection(
      content,
      selection
    );

  if (
    !content ||
    !range ||
    range.crossesPageBreak
  ) {
    return null;
  }

  const pieces = [];

  for (
    let index =
      range.startBlockIndex;
    index <=
      range.endBlockIndex;
    index += 1
  ) {
    const block =
      content.blocks[index];

    const text =
      blockTextFromRuns(
        block?.runs
      );

    let start = 0;
    let end = text.length;

    if (
      index ===
      range.startBlockIndex
    ) {
      start =
        range.startOffset;
    }

    if (
      index ===
      range.endBlockIndex
    ) {
      end =
        range.endOffset;
    }

    pieces.push(
      text.slice(
        start,
        Math.max(start, end)
      )
    );
  }

  return pieces.join("\n\n");
}

/*
 * Replace one normalized manuscript selection with clean plain text.
 *
 * The selection may span several structured manuscript blocks.
 * Existing block identity is preserved at the start boundary.
 * Explicit page breaks fail closed rather than being silently deleted.
 */
export function replaceManuscriptEditorSurfaceSelection(
  value,
  selection = {},
  requestedText = ""
) {
  const content =
    normalizedContentOrNull(value);

  const range =
    normalizeManuscriptEditorSurfaceSelection(
      content,
      selection
    );

  if (
    !content ||
    !range ||
    range.crossesPageBreak
  ) {
    return null;
  }

  if (
    range.startBlockIndex ===
    range.endBlockIndex
  ) {
    return insertManuscriptEditorPlainTextAtSelection(
      content,
      range.startBlockId,
      range.startOffset,
      range.endOffset,
      requestedText
    );
  }

  const startBlock =
    content.blocks[
      range.startBlockIndex
    ];

  const endBlock =
    content.blocks[
      range.endBlockIndex
    ];

  if (
    !startBlock ||
    !endBlock ||
    startBlock.type === "page_break" ||
    endBlock.type === "page_break"
  ) {
    return null;
  }

  const startRuns =
    normalizeManuscriptEditorRuns(
      startBlock.runs
    );

  const endRuns =
    normalizeManuscriptEditorRuns(
      endBlock.runs
    );

  const endLength =
    blockTextFromRuns(
      endRuns
    ).length;

  const before =
    sliceRunsBetweenOffsets(
      startRuns,
      0,
      range.startOffset
    );

  const after =
    sliceRunsBetweenOffsets(
      endRuns,
      range.endOffset,
      endLength
    );

  const split =
    splitRunsAtOffset(
      startRuns,
      range.startOffset
    );

  const insertionMarks =
    split.before[
      split.before.length - 1
    ] ||
    split.after[0] ||
    emptyRun();

  const text =
    String(requestedText || "")
      .replace(/\u200b/g, "")
      .replace(/\r\n?/g, "\n")
      .replace(/\f/g, "");

  const paragraphs =
    text.split("\n\n");

  function insertionRuns(
    paragraphText
  ) {
    return normalizeManuscriptEditorRuns([
      plainTextRun(
        paragraphText,
        insertionMarks
      ),
    ]);
  }

  const used =
    new Set(
      content.blocks.map(
        (block) =>
          String(block?.id || "")
      )
    );

  const replacementBlocks = [];

  if (paragraphs.length === 1) {
    replacementBlocks.push({
      ...startBlock,
      runs:
        normalizeManuscriptEditorRuns([
          ...before,
          ...insertionRuns(
            paragraphs[0]
          ),
          ...after,
        ]),
    });
  } else {
    replacementBlocks.push({
      ...startBlock,
      runs:
        normalizeManuscriptEditorRuns([
          ...before,
          ...insertionRuns(
            paragraphs[0]
          ),
        ]),
    });

    paragraphs
      .slice(1)
      .forEach(
        (
          paragraphText,
          paragraphIndex,
          remaining
        ) => {
          const id =
            nextBlockIdFromUsed(
              used
            );

          const isLast =
            paragraphIndex ===
            remaining.length - 1;

          const runs =
            normalizeManuscriptEditorRuns([
              ...insertionRuns(
                paragraphText
              ),
              ...(isLast
                ? after
                : []),
            ]);

          replacementBlocks.push(
            continuationBlock(
              startBlock,
              id,
              runs
            )
          );
        }
      );
  }

  const blocks = [
    ...content.blocks.slice(
      0,
      range.startBlockIndex
    ),
    ...replacementBlocks,
    ...content.blocks.slice(
      range.endBlockIndex + 1
    ),
  ];

  const nextContent =
    normalizedContentOrNull({
      version:
        MANUSCRIPT_CONTENT_VERSION,
      blocks,
    });

  if (!nextContent) {
    return null;
  }

  const finalBlock =
    replacementBlocks[
      replacementBlocks.length - 1
    ];

  const finalInsertedText =
    paragraphs[
      paragraphs.length - 1
    ] || "";

  const caretOffset =
    paragraphs.length === 1
      ? blockTextFromRuns(before)
          .length +
        paragraphs[0].length
      : finalInsertedText.length;

  return {
    content: nextContent,
    selection: {
      blockId:
        finalBlock.id,
      start:
        caretOffset,
      end:
        caretOffset,
    },
  };
}

/*
 * Toggle one inline emphasis mark across a normalized manuscript-surface
 * selection. For a multi-block selection, toggle semantics are determined
 * from the complete selected range:
 *
 * - if every selected character already has the mark, remove it everywhere;
 * - otherwise apply it everywhere.
 *
 * Paragraph identity/presentation and unselected runs remain untouched.
 * Explicit page breaks fail closed.
 */
export function toggleManuscriptEditorSurfaceInlineMark(
  value,
  selection = {},
  mark
) {
  const content =
    normalizedContentOrNull(value);

  if (!content) {
    return null;
  }

  if (
    !["bold", "italic", "underline", "highlight"].includes(
      mark
    )
  ) {
    return content;
  }

  const range =
    normalizeManuscriptEditorSurfaceSelection(
      content,
      selection
    );

  if (
    !range ||
    range.crossesPageBreak
  ) {
    return null;
  }

  if (
    range.startBlockIndex ===
    range.endBlockIndex
  ) {
    return toggleManuscriptEditorInlineMark(
      content,
      range.startBlockId,
      range.startOffset,
      range.endOffset,
      mark
    );
  }

  let anySelected = false;
  let allMarked = true;

  for (
    let index =
      range.startBlockIndex;
    index <=
      range.endBlockIndex;
    index += 1
  ) {
    const block =
      content.blocks[index];

    if (
      !block ||
      block.type === "page_break"
    ) {
      return null;
    }

    const runs =
      normalizeManuscriptEditorRuns(
        block.runs
      );

    const total =
      runs.reduce(
        (sum, run) =>
          sum + run.text.length,
        0
      );

    const start =
      index ===
      range.startBlockIndex
        ? range.startOffset
        : 0;

    const end =
      index ===
      range.endBlockIndex
        ? range.endOffset
        : total;

    if (end <= start) {
      continue;
    }

    let cursor = 0;

    runs.forEach((run) => {
      const runStart = cursor;
      const runEnd =
        runStart +
        run.text.length;

      cursor = runEnd;

      if (
        runEnd <= start ||
        runStart >= end
      ) {
        return;
      }

      anySelected = true;

      if (!run[mark]) {
        allMarked = false;
      }
    });
  }

  if (!anySelected) {
    return content;
  }

  const targetValue =
    !allMarked;

  const blocks =
    content.blocks.map(
      (block, index) => {
        if (
          index <
            range.startBlockIndex ||
          index >
            range.endBlockIndex
        ) {
          return block;
        }

        if (
          !block ||
          block.type ===
            "page_break"
        ) {
          return block;
        }

        const runs =
          normalizeManuscriptEditorRuns(
            block.runs
          );

        const total =
          runs.reduce(
            (sum, run) =>
              sum +
              run.text.length,
            0
          );

        const start =
          index ===
          range.startBlockIndex
            ? range.startOffset
            : 0;

        const end =
          index ===
          range.endBlockIndex
            ? range.endOffset
            : total;

        if (end <= start) {
          return block;
        }

        return {
          ...block,
          runs:
            applyMarkToRuns(
              runs,
              start,
              end,
              mark,
              targetValue
            ),
        };
      }
    );

  return normalizedContentOrNull({
    version:
      MANUSCRIPT_CONTENT_VERSION,
    blocks,
  });
}


/*
 * Apply one supported typography property across a normalized
 * manuscript-surface selection while preserving paragraph identity,
 * presentation, unselected characters and unrelated inline marks.
 *
 * Explicit page breaks fail closed.
 */
export function setManuscriptEditorSurfaceTypography(
  value,
  selection = {},
  property,
  requestedValue
) {
  const content =
    normalizedContentOrNull(value);

  if (!content) {
    return null;
  }

  if (
    ![
      "fontFamily",
      "fontSize",
    ].includes(property)
  ) {
    return content;
  }

  const range =
    normalizeManuscriptEditorSurfaceSelection(
      content,
      selection
    );

  if (
    !range ||
    range.crossesPageBreak
  ) {
    return null;
  }

  if (
    range.startBlockIndex ===
    range.endBlockIndex
  ) {
    return setManuscriptEditorInlineTypography(
      content,
      range.startBlockId,
      range.startOffset,
      range.endOffset,
      property,
      requestedValue
    );
  }

  const normalizedValue =
    property === "fontFamily"
      ? (
          normalizedFontFamily(
            requestedValue
          ) || undefined
        )
      : (
          normalizedFontSize(
            requestedValue
          ) || undefined
        );

  const blocks =
    content.blocks.map(
      (block, index) => {
        if (
          index <
            range.startBlockIndex ||
          index >
            range.endBlockIndex
        ) {
          return block;
        }

        if (
          !block ||
          block.type ===
            "page_break"
        ) {
          return block;
        }

        const runs =
          normalizeManuscriptEditorRuns(
            block.runs
          );

        const total =
          runs.reduce(
            (sum, run) =>
              sum +
              run.text.length,
            0
          );

        const start =
          index ===
          range.startBlockIndex
            ? range.startOffset
            : 0;

        const end =
          index ===
          range.endBlockIndex
            ? range.endOffset
            : total;

        if (end <= start) {
          return block;
        }

        return {
          ...block,
          runs:
            applyTypographyToRuns(
              runs,
              start,
              end,
              property,
              normalizedValue
            ),
        };
      }
    );

  return normalizedContentOrNull({
    version:
      MANUSCRIPT_CONTENT_VERSION,
    blocks,
  });
}
